import { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import { tallyTaxApi, taxMapBusy, type TallyTaxMapRow, type TallyTaxState } from "../../data/tallyTaxApi";
import { Callout } from "../../components/Callout";
import { SubStepShell } from "../../components/SubStepShell";
import { CoButton } from "../../components/Button";
import { useSubStepNav } from "../useSubStepNav";
import { TallyTaxMapTable } from "./TallyTaxMapTable";

const INPUT_CLASS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

export function TallyTdsStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "tds-tcs-review");
  const [tax, setTax] = useState<TallyTaxState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void tallyTaxApi.state(clientId).then(
      (next) => {
        if (!cancelled) setTax(next);
      },
      (reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load TDS and TCS.");
      }
    );

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!tax || !taxMapBusy([...tax.tds, ...tax.tcs])) return undefined;
    const timer = window.setInterval(() => {
      void tallyTaxApi.state(clientId).then(setTax).catch(() => undefined);
    }, 2000);

    return () => window.clearInterval(timer);
  }, [clientId, tax]);

  async function run(work: () => Promise<TallyTaxState>) {
    setBusy(true);
    setError(null);
    try {
      setTax(await work());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update the tax mapping.");
    } finally {
      setBusy(false);
    }
  }

  const ready = tax?.rates_mapped === true && !busy;
  const clientName = client?.name || "this client";
  const creating = tax !== null && taxMapBusy([...tax.tds, ...tax.tcs]);

  return (
    <SubStepShell
      title="TDS & TCS Settings"
      description={`This is the TDS and TCS catalogue ${clientName}'s Tally company will use. Each rate needs a ledger under Duties & Taxes.`}
      thenWhat={ready ? "Continue → Currency Settings." : "Map every TDS and TCS rate to continue."}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      {error ? <Callout variant="warning">{error}</Callout> : null}

      {tax === null ? (
        <p className="text-sm text-muted-foreground">Reading TDS and TCS…</p>
      ) : (
        <>
          <RateSection
            title="TDS"
            kind="tds"
            rows={tax.tds}
            tax={tax}
            busy={busy}
            creating={creating}
            clientId={clientId}
            onAdded={setTax}
            onError={setError}
            onRun={run}
          />
          <RateSection
            title="TCS"
            kind="tcs"
            rows={tax.tcs}
            tax={tax}
            busy={busy}
            creating={creating}
            clientId={clientId}
            onAdded={setTax}
            onError={setError}
            onRun={run}
          />
        </>
      )}
    </SubStepShell>
  );
}

function RateSection({
  title,
  kind,
  rows,
  tax,
  busy,
  creating,
  clientId,
  onAdded,
  onError,
  onRun,
}: {
  title: string;
  kind: "tds" | "tcs";
  rows: TallyTaxMapRow[];
  tax: TallyTaxState;
  busy: boolean;
  creating: boolean;
  clientId: ClientId;
  onAdded: (next: TallyTaxState) => void;
  onError: (message: string | null) => void;
  onRun: (work: () => Promise<TallyTaxState>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <CoButton type="button" variant="outline" disabled={busy} onClick={() => setOpen(true)}>
          Add {title} rate
        </CoButton>
      </div>
      <TallyTaxMapTable
        rows={rows}
        dutyLedgers={tax.duty_ledgers}
        busy={busy}
        createLocked={creating}
        onMap={(row, name) => void onRun(() => tallyTaxApi.mapLedger(clientId, row.kind, row.id, name))}
        onCreate={(row, name) => void onRun(() => tallyTaxApi.createLedger(clientId, row.kind, row.id, name))}
      />
      <AddRateModal
        open={open}
        kind={kind}
        busy={busy}
        clientId={clientId}
        onClose={() => setOpen(false)}
        onAdded={onAdded}
        onError={onError}
      />
    </section>
  );
}

function AddRateModal({
  open,
  kind,
  busy,
  clientId,
  onClose,
  onAdded,
  onError,
}: {
  open: boolean;
  kind: "tds" | "tcs";
  busy: boolean;
  clientId: ClientId;
  onClose: () => void;
  onAdded: (next: TallyTaxState) => void;
  onError: (message: string | null) => void;
}) {
  const [section, setSection] = useState("");
  const [percentage, setPercentage] = useState("");
  const [applicability, setApplicability] = useState("");
  const [threshold, setThreshold] = useState("");
  const [expired, setExpired] = useState(false);
  const [adding, setAdding] = useState(false);
  const label = kind === "tds" ? "TDS" : "TCS";

  if (!open) return null;

  async function addRate() {
    setAdding(true);
    onError(null);
    try {
      onAdded(
        await tallyTaxApi.addRate(clientId, {
          kind,
          section: section.trim(),
          percentage: percentage.trim(),
          applicability: applicability.trim(),
          threshold: threshold.trim(),
          expired,
        })
      );
      setSection("");
      setPercentage("");
      setApplicability("");
      setThreshold("");
      setExpired(false);
      onClose();
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : "Could not add that rate.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-lg rounded-lg border bg-popover p-5 shadow-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`add-${kind}-title`}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={`add-${kind}-title`} className="text-sm font-semibold">
          Add a {label} rate
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Applicability is a short description of who the section covers. Mark the rate expired when it is no longer
          current but an older bill can still use it. After you add it, map it to a ledger under Duties and Taxes, or
          create that ledger in Tally.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Section
            <input
              className={INPUT_CLASS}
              value={section}
              maxLength={32}
              onChange={(event) => setSection(event.target.value)}
              placeholder={kind === "tds" ? "194J" : "206C(1H)"}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Percentage
            <input className={INPUT_CLASS} value={percentage} onChange={(event) => setPercentage(event.target.value)} placeholder="10" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Applicability
            <input
              className={INPUT_CLASS}
              value={applicability}
              onChange={(event) => setApplicability(event.target.value)}
              placeholder={kind === "tds" ? "Professional fees" : "Sale of goods"}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Threshold
            <input className={INPUT_CLASS} value={threshold} onChange={(event) => setThreshold(event.target.value)} placeholder="50000" />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={expired} onChange={(event) => setExpired(event.target.checked)} />
            Expired rate
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <CoButton type="button" variant="outline" onClick={onClose}>
            Cancel
          </CoButton>
          <CoButton
            type="button"
            disabled={busy || adding || section.trim() === "" || percentage.trim() === ""}
            onClick={() => void addRate()}
          >
            Add rate
          </CoButton>
        </div>
      </div>
    </div>
  );
}
